import React, { useState, useEffect, useCallback  } from 'react';
import styled, { keyframes } from 'styled-components';
import { Check, Download, Loader } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCart } from '../hooks/useCart';


const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 20px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px;
  text-align: center;
  max-width: 500px;
  width: 100%;
`;

const Title = styled.h1`
  color: #2c3e50;
  font-size: 32px;
  margin-bottom: 20px;
`;

const Message = styled.p`
  color: #34495e;
  font-size: 18px;
  margin-bottom: 30px;
`;

const CheckIcon = styled(Check)`
  color: #2ecc71;
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.disabled ? '#bdc3c7' : '#3498db'};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 18px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${props => props.disabled ? '#bdc3c7' : '#2980b9'};
  }

  svg {
    margin-right: 10px;
  }
`;

const OrderId = styled.p`
  font-size: 16px;
  color: #7f8c8d;
  margin-top: 20px;
`;

const LoadingSpinner = styled(Loader)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    100% { transform: rotate(360deg); }
  }
`;

const ThankYouPage = () => {
  console.log('ThankYouPage render');
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [orderId, setOrderId] = useState(null);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('ThankYouPage mounted');
    return () => {
      console.log('ThankYouPage unmounted');
    };
  }, []);

  useEffect(() => {
    console.log('Clearing cart effect');
    const pendingOrderId = localStorage.getItem('pendingOrderId');
    if (pendingOrderId) {
      clearCart();
      localStorage.removeItem('pendingOrderId');
      console.log('Cart cleared and pendingOrderId removed:', pendingOrderId);
    }
  }, [clearCart]);

  const verifyPayment = useCallback(async (orderIdFromQuery) => {
    console.log('verifyPayment called', orderIdFromQuery);
    if (isVerified) return;

    try {
      setIsLoading(true);
      console.log('Verifying payment for order:', orderIdFromQuery);
      const response = await fetch(`/api/verify-payment/?order_id=${orderIdFromQuery}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      console.log('Payment verification response:', data);

      if (data.success) {
        setOrderId(orderIdFromQuery);
        setPaymentStatus(data.payment_method);
        setIsVerified(true);
        // toast.success('Order processed successfully!');
        console.log('Order processed successfully!');
      } else {
        throw new Error(data.error || 'Order processing failed.');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
      // toast.error(error.message);
      console.error('Order processing error:', error.message);
    } finally {
      setIsLoading(false);
    }
  }, [isVerified]);

  useEffect(() => {
    console.log('Order verification effect');
    const queryParams = new URLSearchParams(location.search);
    const orderIdFromQuery = queryParams.get('order_id');

    console.log('Order ID:', orderIdFromQuery);

    if (orderIdFromQuery && !isVerified) {
      verifyPayment(orderIdFromQuery);
    } else if (location.state && location.state.orderId && !isVerified) {
      setOrderId(location.state.orderId);
      setPaymentStatus('cash');
      setIsVerified(true);
      setIsLoading(false);
    } else if (!orderIdFromQuery && !location.state?.orderId) {
      setError('No order information found');
      setIsLoading(false);
    }
  }, [location, verifyPayment, isVerified]);

  const downloadInvoice = async () => {
    if (orderId && !isDownloaded) {
      try {
        const response = await fetch(`/api/download-invoice/${orderId}/`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice_${orderId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setIsDownloaded(true);
        toast.success('Your invoice has been downloaded successfully!');
      } catch (error) {
        console.error('Error downloading invoice:', error);
        toast.error('There was an error downloading the invoice. Please try again.');
      }
    }
  };

  console.log('Before render - isLoading:', isLoading, 'error:', error, 'orderId:', orderId);

  try {
    if (isLoading) {
      return (
        <Container>
          <Card>
            <LoadingSpinner size={50} />
            <Message>Verifying your order...</Message>
          </Card>
        </Container>
      );
    }

    if (error) {
      return (
        <Container>
          <Card>
            <Message>{error}</Message>
            <DownloadButton onClick={() => navigate('/checkout')}>
              Return to Checkout
            </DownloadButton>
          </Card>
        </Container>
      );
    }

    if (!orderId) {
      return (
        <Container>
          <Card>
            <Message>Order ID is missing. Please check your order confirmation.</Message>
            <DownloadButton onClick={() => navigate('/checkout')}>
              Go to Checkout
            </DownloadButton>
          </Card>
        </Container>
      );
    }

    return (
      <Container>
        {/* <ToastContainer /> */}
        <Card>
          <CheckIcon />
          <Title>Thank You for Your Order!</Title>
          <Message>
            {paymentStatus === 'paid'
              ? 'Your payment has been processed successfully.'
              : 'Your order has been placed and will be processed upon delivery.'}
          </Message>
          <DownloadButton onClick={downloadInvoice} disabled={isDownloaded}>
            <Download size={20} />
            {isDownloaded ? 'Invoice Downloaded' : 'Download Invoice'}
          </DownloadButton>
          {orderId && <OrderId>Order ID: {orderId}</OrderId>}
        </Card>
      </Container>
    );
  } catch (renderError) {
    console.error('Render error:', renderError);
    return (
      <Container>
        <Card>
          <Message>An error occurred while rendering the page. Please try again later.</Message>
          <DownloadButton onClick={() => navigate('/checkout')}>
            Return to Checkout
          </DownloadButton>
        </Card>
      </Container>
    );
  }
};

export default ThankYouPage;